import React from 'react';


const ComputationalThinkingPage = () => {
  return (
    <div className="App">

      <section className="section" style={{ backgroundColor: 'var(--light)' }}>
        <div className="container">
          <div className="section-title">
            <h2>Computational Thinking Research</h2>
            <p>The theoretical foundation of the Nozomi Robotics approach</p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: '3fr 2fr',
            gap: '3rem',
            alignItems: 'start'
          }}>
            <div>
              <h3 style={{ color: 'var(--primary)', marginBottom: '1.5rem' }}>Understanding Computational Thinking</h3>

              <p style={{ marginBottom: '1rem' }}>
                Computational thinking (CT) is a problem-solving approach rooted in computer science principles that has become
                increasingly recognized as a fundamental skill for everyone, not just computer scientists. First popularized
                by Jeannette Wing in her seminal 2006 paper, computational thinking involves breaking down complex problems
                into manageable parts, recognizing patterns, developing abstractions, and designing algorithms.
              </p>

              <p style={{ marginBottom: '1rem' }}>
                At Nozomi Robotics, we operationalize computational thinking through four key components:
              </p>

              <ul style={{
                listStyleType: 'none',
                padding: '0',
                margin: '1.5rem 0'
              }}>
                <li style={{
                  padding: '0.5rem 0 0.5rem 2rem',
                  position: 'relative',
                  marginBottom: '1rem'
                }}>
                  <span style={{
                    position: 'absolute',
                    left: '0',
                    color: 'var(--secondary)',
                    fontWeight: 'bold'
                  }}>1.</span>
                  <strong>Decomposition:</strong> Breaking complex problems into smaller, manageable sub-problems.
                  In Nozomi Robotics, learners decompose robot behaviors into specific movements and logic sequences.
                </li>

                <li style={{
                  padding: '0.5rem 0 0.5rem 2rem',
                  position: 'relative',
                  marginBottom: '1rem'
                }}>
                  <span style={{
                    position: 'absolute',
                    left: '0',
                    color: 'var(--secondary)',
                    fontWeight: 'bold'
                  }}>2.</span>
                  <strong>Pattern Recognition:</strong> Identifying similarities or patterns within and across problems.
                  Our challenges guide learners to recognize recurring patterns in robot programming tasks.
                </li>

                <li style={{
                  padding: '0.5rem 0 0.5rem 2rem',
                  position: 'relative',
                  marginBottom: '1rem'
                }}>
                  <span style={{
                    position: 'absolute',
                    left: '0',
                    color: 'var(--secondary)',
                    fontWeight: 'bold'
                  }}>3.</span>
                  <strong>Abstraction:</strong> Filtering out unnecessary details and focusing on relevant information.
                  Users learn to create reusable function blocks that abstract away complexity.
                </li>

                <li style={{
                  padding: '0.5rem 0 0.5rem 2rem',
                  position: 'relative'
                }}>
                  <span style={{
                    position: 'absolute',
                    left: '0',
                    color: 'var(--secondary)',
                    fontWeight: 'bold'
                  }}>4.</span>
                  <strong>Algorithmic Thinking:</strong> Developing step-by-step solutions to problems.
                  Nozomi Robotics challenges require creating precise sequences of instructions for robots to follow.
                </li>
              </ul>

              <h3 style={{ color: 'var(--primary)', marginTop: '2rem', marginBottom: '1.5rem' }}>Why Physical Computing for CT?</h3>

              <p style={{ marginBottom: '1rem' }}>
                Research has shown that physical computing—programming physical objects in the real world—offers unique
                benefits for developing computational thinking skills:
              </p>

              <ul style={{
                listStyleType: 'none',
                padding: '0',
                margin: '1.5rem 0'
              }}>
                <li style={{
                  padding: '0.5rem 0 0.5rem 2rem',
                  position: 'relative',
                  marginBottom: '1rem'
                }}>
                  <span style={{
                    position: 'absolute',
                    left: '0',
                    color: 'var(--accent)',
                    fontWeight: 'bold'
                  }}>•</span>
                  <strong>Immediate Feedback:</strong> When programming a physical robot, errors become immediately
                  apparent through the robot's behavior, providing concrete feedback that helps learners debug their thinking.
                </li>

                <li style={{
                  padding: '0.5rem 0 0.5rem 2rem',
                  position: 'relative',
                  marginBottom: '1rem'
                }}>
                  <span style={{
                    position: 'absolute',
                    left: '0',
                    color: 'var(--accent)',
                    fontWeight: 'bold'
                  }}>•</span>
                  <strong>Embodied Cognition:</strong> Physical objects engage multiple sensory modalities, creating stronger
                  cognitive connections than screen-based activities alone.
                </li>

                <li style={{
                  padding: '0.5rem 0 0.5rem 2rem',
                  position: 'relative',
                  marginBottom: '1rem'
                }}>
                  <span style={{
                    position: 'absolute',
                    left: '0',
                    color: 'var(--accent)',
                    fontWeight: 'bold'
                  }}>•</span>
                  <strong>Boundary Objects:</strong> Robots serve as "boundary objects" that help bridge the abstract world
                  of programming with the physical world, making computational concepts more accessible.
                </li>

                <li style={{
                  padding: '0.5rem 0 0.5rem 2rem',
                  position: 'relative'
                }}>
                  <span style={{
                    position: 'absolute',
                    left: '0',
                    color: 'var(--accent)',
                    fontWeight: 'bold'
                  }}>•</span>
                  <strong>Intrinsic Motivation:</strong> The satisfaction of making a physical object move and respond increases
                  engagement and persistence through challenging problems.
                </li>
              </ul>

              <h3 style={{ color: 'var(--primary)', marginTop: '2rem', marginBottom: '1.5rem' }}>Assessment and Learning Progression</h3>

              {/* <p style={{ marginBottom: '1rem' }}>
                Following the work of Dr. Shima Salehi at Stanford's IDEAL Lab, Nozomi Robotics incorporates assessment approaches
                that focus not just on the final product, but on the computational thinking processes used along the way.
              </p> */}

              <p style={{ marginBottom: '1rem' }}>
                Our learning challenges are structured to progressively develop computational thinking skills through a
                scaffolded approach:
              </p>

              <ol style={{ paddingLeft: '1.5rem', marginBottom: '1.5rem' }}>
                <li style={{ marginBottom: '0.5rem' }}>
                  <strong>Use phase:</strong> Learners first follow existing algorithms to control the robot
                </li>
                <li style={{ marginBottom: '0.5rem' }}>
                  <strong>Modify phase:</strong> Learners adapt existing code to solve similar problems
                </li>
                <li style={{ marginBottom: '0.5rem' }}>
                  <strong>Create phase:</strong> Learners design original solutions to novel problems
                </li>
                <li>
                  <strong>Transfer phase:</strong> Learners apply CT skills to problems in different domains
                </li>
              </ol>
{/*
              <div style={{
                marginTop: '2.5rem',
                padding: '1.5rem',
                backgroundColor: 'white',
                borderRadius: '8px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                borderLeft: '4px solid var(--primary)'
              }}>
                <h4 style={{ marginBottom: '1rem', color: 'var(--dark)' }}>Key Research References:</h4>
                <ul style={{ fontSize: '0.9rem', paddingLeft: '1.5rem' }}>
                  <li style={{ marginBottom: '0.5rem' }}>
                    Wing, J. M. (2006). Computational thinking. Communications of the ACM, 49(3), 33-35.
                  </li>
                  <li style={{ marginBottom: '0.5rem' }}>
                    Grover, S., & Pea, R. (2013). Computational thinking in K–12: A review of the state of the field. Educational Researcher, 42(1), 38-43.
                  </li>
                  <li style={{ marginBottom: '0.5rem' }}>
                    Salehi, S., et al. (2020). Examining the relation between computational thinking and problem solving. Journal of Educational Computing Research.
                  </li>
                  <li style={{ marginBottom: '0.5rem' }}>
                    Przybylla, M., & Romeike, R. (2014). Physical computing and its scope—towards a constructionist computer science curriculum with physical computing. Informatics in Education, 13(2), 241-254.
                  </li>
                  <li>
                    Kafai, Y. B., & Burke, Q. (2014). Connected code: Why children need to learn programming. MIT Press.
                  </li>
                </ul>
              </div> */}
            </div>

            <div>
              <div style={{
                position: 'sticky',
                top: '100px',
                backgroundColor: 'white',
                borderRadius: '8px',
                boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
                padding: '1.5rem',
                borderTop: '4px solid var(--secondary)'
              }}>
                <h4 style={{ textAlign: 'center', marginBottom: '1rem', color: 'var(--dark)' }}>Computational Thinking Framework</h4>

                {/* <img
                  src="/api/placeholder/400/320"
                  alt="Computational Thinking Framework"
                  style={{ width: '100%', height: 'auto', borderRadius: '4px', marginBottom: '1rem' }}
                /> */}

                <p style={{
                  textAlign: 'center',
                  fontSize: '0.9rem',
                  color: '#666',
                  fontStyle: 'italic',
                  marginBottom: '1.5rem'
                }}>
                  Nozomi Robotics's approach to developing computational thinking skills through robotics
                </p>

                <div style={{
                  backgroundColor: 'var(--light)',
                  padding: '1rem',
                  borderRadius: '8px'
                }}>
                  <h5 style={{ marginBottom: '0.5rem', color: 'var(--primary)' }}>Assessment Approach</h5>
                  <p style={{ fontSize: '0.9rem' }}>
                    Our challenges evaluate both product (what learners create) and process (how they approach problems),
                    focusing on computational thinking skills that transfer beyond robotics.
                  </p>
                </div>
              </div>

              {/* <div style={{
                backgroundColor: 'white',
                borderRadius: '8px',
                boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
                padding: '1.5rem',
                marginTop: '2rem',
                borderTop: '4px solid var(--accent)'
              }}>
                <h4 style={{ marginBottom: '1rem', color: 'var(--dark)' }}>Want to Learn More?</h4>
                <p style={{ marginBottom: '1.5rem' }}>
                  Explore additional resources on computational thinking and how it can be applied across various contexts.
                </p>
                <ul style={{ listStyleType: 'none', padding: '0' }}>
                  <li style={{ marginBottom: '1rem' }}>
                    <a href="#" style={{
                      display: 'block',
                      padding: '0.8rem',
                      backgroundColor: 'var(--light)',
                      borderRadius: '4px',
                      textDecoration: 'none',
                      color: 'var(--dark)',
                      fontWeight: '500'
                    }}>
                      📄 Computational Thinking White Paper
                    </a>
                  </li>
                  <li style={{ marginBottom: '1rem' }}>
                    <a href="#" style={{
                      display: 'block',
                      padding: '0.8rem',
                      backgroundColor: 'var(--light)',
                      borderRadius: '4px',
                      textDecoration: 'none',
                      color: 'var(--dark)',
                      fontWeight: '500'
                    }}>
                      🎓 Educator Resources
                    </a>
                  </li>
                  <li>
                    <a href="#" style={{
                      display: 'block',
                      padding: '0.8rem',
                      backgroundColor: 'var(--light)',
                      borderRadius: '4px',
                      textDecoration: 'none',
                      color: 'var(--dark)',
                      fontWeight: '500'
                    }}>
                      🤖 CT Challenge Cards
                    </a>
                  </li>
                </ul>
              </div> */}
            </div>
          </div>
        </div>
      </section>

    </div>
  );
};

export default ComputationalThinkingPage;